// AuthSidebar.jsx (for CRA)
import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect } from 'react';
import { FaShoppingBag } from 'react-icons/fa';
import { FaLock, FaPowerOff } from 'react-icons/fa6';
import { IoPersonCircleOutline } from "react-icons/io5";
import { RiDashboardFill } from 'react-icons/ri';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/UserContext';
import utils from '../../utils';
import { useCart } from '../../context/CartContext';

// Helper: determine active link (CRA version)
const isActive = (currentPath, href) => {
    if (href === '/auth') return currentPath === '/auth';
    return currentPath.startsWith(href) && href !== '/';
};

function AuthSidebar() {
    const {resetCart} = useCart()
    const { isAuth, logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;

    useEffect(() => {
        if (!isAuth) {
            navigate('/auth/login', { replace: true });
        }
    }, [isAuth, navigate]);

    const baseNavItems = [
        { href: '/auth/profile', label: 'Profile', icon: <IoPersonCircleOutline className="h-6 w-6" /> },
        { href: '/auth/access', label: 'Account', icon: <FaLock className="h-5 w-5" /> },
    ];

    const customerNavItems = [
        { href: '/auth/orders', label: 'Orders', icon: <FaShoppingBag className="h-5 w-5" /> },
        // { href: '/auth/address', label: 'Manage Address', icon: <FaLocationDot className="h-5 w-5" /> },
    ];

    const navItems = [
        ...baseNavItems,
        ...(user?.is_admin || user?.is_vendor ? [] : customerNavItems),
    ];

    const performLogout = () => {
        logout();
        resetCart();
        navigate('/auth/login', { replace: true });
        setTimeout(() => {
            toast.success("Successfully logged out");
        }, 400);
    };

    return (
        <aside className="w-full md:w-64 lg:w-72 flex-shrink-0">
            <div className="bg-white p-6 rounded-lg shadow-md sticky top-1">
                <div className="flex items-center pb-4 mb-4 border-b border-gray-200 gap-3">
                    <div className="avatar placeholder">
                        <div className="bg-neutral text-neutral-content w-14 rounded-full">
                            <span className="text-3xl">{utils.makeProfileLetter(user)}</span>
                        </div>
                    </div>
                    <div>
                        {user?.full_name && (
                            <h3 className="font-semibold text-gray-800">{user.full_name}</h3>
                        )}
                        <p className="text-sm text-gray-600">{user?.email}</p>
                    </div>
                </div>

                <nav className="space-y-2">
                    {navItems.map((item) => {
                        const active = isActive(currentPath, item.href);
                        return (
                            <Link
                                key={item.href}
                                className={`flex items-center px-4 py-2.5 font-medium rounded-md transition-colors ${active
                                    ? 'bg-primary/10 text-primary font-semibold'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    }`}
                                to={item.href}
                            >
                                <span className="mr-3">{item.icon}</span>
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}

                    {(user?.is_vendor || user?.is_admin) && (
                        <Link
                            className="flex items-center px-4 py-2.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium rounded-md transition-colors"
                            to={user?.is_vendor ? "/dashboard/vendor" : "/dashboard/admin"}
                        >
                            <span className="mr-3"><RiDashboardFill className="h-5 w-5" /></span>
                            <span>Dashboard</span>
                        </Link>
                    )}

                    <div
                        onClick={performLogout}
                        className="flex items-center px-4 py-2.5 text-red-600 hover:bg-red-50 font-medium rounded-md transition-colors cursor-pointer"
                    >
                        <span className="mr-3"><FaPowerOff className="h-5 w-5" /></span>
                        <span>Logout</span>
                    </div>
                </nav>
            </div>
        </aside>
    );
}

export default AuthSidebar;